import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { CapturedId, IdCapture, IdCaptureError, IdSide, RejectionReason } from '@scandit/web-datacapture-id';
import { IIdDocumentData, IScanditIdCaptureProps } from './scandit-id-capture.types';
import { DateResultJSON } from '@scandit/web-datacapture-id/build/js/SerializedTypes';
import dayjs from 'dayjs';
import { DATE_YYYY_MM_DD } from 'common/constans/date.constants';
import idDocumentRecognitionContext, {
  IdDocumentRecognitionSDKStatus,
} from 'features/eiq-secure/stores/id-document-recognition/id-document-recognition-context';
import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import './scandit-id-capture.scss';
import configurationStore from 'common/stores/configuration/configuration.store';
import mediaServerContext from 'features/eiq-secure/stores/id-document-recognition/media-server-context';

const ScanditIdCapture = observer(({ onRecognized, onFailed }: IScanditIdCaptureProps) => {
  const scannerRef = useRef<any>(null);

  const dateResultJSONToString = (dateResult: DateResultJSON | null) => {
    if (!dateResult?.year || !dateResult.month || !dateResult?.day) {
      return null;
    }
    const date = dayjs(new Date(dateResult.year, dateResult.month, dateResult.day));
    return date.format(DATE_YYYY_MM_DD);
  };

  const mapToIdDocumentModel = (capturedId: CapturedId): IIdDocumentData => {
    const obj = capturedId.toJSONObject();
    let image = capturedId.images.getCroppedDocument(IdSide.Front);

    if (!image) {
      image = capturedId.images.getFrame(IdSide.Front);
    }

    return {
      type: capturedId.document!.documentType,
      imageBase64: image!,
      firstName: obj.firstName,
      lastName: obj.lastName,
      gender: obj.sex,
      dateOfBirth: dateResultJSONToString(obj.dateOfBirth),
      nationality: obj.nationality,
      address: obj.address,
      issuingCountry: obj.issuingCountry,
      issuingCountryIso: obj.issuingCountryIso,
      number: obj.documentNumber,
      additionalNumber: obj.documentAdditionalNumber,
      expiredAt: dateResultJSONToString(obj.dateOfExpiry),
      issuedAt: dateResultJSONToString(obj.dateOfIssue),
    };
  };

  const onScan = {
    didCaptureId: (capturedId: CapturedId) => {
      const idDocument = mapToIdDocumentModel(capturedId);
      onRecognized(idDocument);
    },
    didRejectId: (apturedId: CapturedId, reason: RejectionReason) => {
      onFailed('Id document is not recognized, try again');
    },
    didFailWithError: (idCapture: IdCapture, error: IdCaptureError) => {
      onFailed(error.message);
    },
  };

  const cancel = useRef(false);

  const enableScanning = async () => {
    if (cancel.current) return;
    idDocumentRecognitionContext.idCapture?.addListener(onScan);
    if (cancel.current) return;
    await idDocumentRecognitionContext.connectToElement(scannerRef.current);
    if (cancel.current) return;
    await idDocumentRecognitionContext.enableScanning(true);
  };

  const [showLoader, setShowLoader] = useState(true);
  const videoRef = useRef<any>(null);
  const scanFromVideoRef = useRef(true);

  useEffect(() => {
    if (configurationStore.useRtspStream) {
      return;
    }

    if (idDocumentRecognitionContext.status === IdDocumentRecognitionSDKStatus.Ready) {
      enableScanning();
      setShowLoader(false);
    }
  }, [idDocumentRecognitionContext.status]);

  const scanFromVideo = async () => {
    await idDocumentRecognitionContext.scanStream(videoRef);

    const intervalId = setInterval(async () => {
      if (!scanFromVideoRef.current) {
        clearInterval(intervalId);
        return;
      }

      await idDocumentRecognitionContext.scanStream(videoRef);
    }, 250);
  };

  const onPlay = async () => {
    setShowLoader(false);
    idDocumentRecognitionContext.idCapture?.addListener(onScan);
    await idDocumentRecognitionContext.connectToElement(videoRef.current);
    scanFromVideoRef.current = true;
    await scanFromVideo();
  };

  useEffect(() => {
    if (!configurationStore.useRtspStream) {
      cancel.current = false;
      enableScanning();
      setShowLoader(false);
    } else {
      mediaServerContext.start(videoRef.current, configurationStore.currentUserGate!.rtspUrl!);
      videoRef.current.addEventListener('playing', onPlay);
    }

    return () => {
      if (configurationStore.useRtspStream) {
        mediaServerContext.stop();
        videoRef.current?.removeEventListener('playing', onPlay);
        scanFromVideoRef.current = false;
      } else {
        cancel.current = true;
        idDocumentRecognitionContext.enableScanning(false);
        idDocumentRecognitionContext.idCapture?.removeListener(onScan);
      }
    };
  }, []);

  return (
    <div className="scandit-scanner-wrapper" id="scandit-scanner-wrapper-id">
      {configurationStore.useRtspStream ? (
        <video ref={videoRef} id="video" autoPlay width="100%" height="290px" poster="img/webrtc.png"></video>
      ) : (
        <div className="scandit-scanner" ref={scannerRef}></div>
      )}
      {showLoader && (
        <Spin className="scandit-scanner-spin" indicator={<LoadingOutlined style={{ fontSize: 64, color: '#AEB5BE' }} spin />} />
      )}
    </div>
  );
});

export default ScanditIdCapture;
